import { useEffect, useState } from "preact/hooks";
import { api } from "./api";
import { useLive } from "./live";
import { parseHash } from "./router";
import { FLAG_KEYS } from "../shared/flags";
import type { FlagKey, Flags } from "../shared/types";
import {
  HOUSEHOLD_EVENT,
  HOUSEHOLD_LOADED_EVENT,
  LOCAL_FLAGS,
  RECENT_APPLY_MS,
  applyHousehold,
  applyHouseholdFlags,
  crownedPortraitUrl,
  currentFlags,
  getHousehold,
  getHouseholdFlag,
  getLastAppliedAt,
  getLocalFlag,
  isFeatureEnabled,
  isHouseholdLoaded,
  setLocalFlag,
} from "./flags-core.ts";

export { applyHouseholdFlags, crownedPortraitUrl, currentFlags, getLocalFlag, isFeatureEnabled, setLocalFlag };

/** This device's own half of a feature switch. Used by the DevSheet's "This device" checkbox. */
export function useLocalFlag(key: FlagKey): boolean {
  const store = LOCAL_FLAGS[key];
  const [visible, setVisible] = useState(store.get);
  useEffect(() => {
    const update = () => setVisible(store.get());
    const onStorage = (e: StorageEvent) => {
      if (e.key !== store.storageKey && e.key !== null) return;
      store.syncFromStorage();
      update();
    };
    window.addEventListener(store.event, update);
    window.addEventListener("storage", onStorage);
    update();
    return () => {
      window.removeEventListener(store.event, update);
      window.removeEventListener("storage", onStorage);
    };
  }, [key]);
  return visible;
}

/**
 * Call once, from the app shell, after the peon's name and auth are known.
 * Loads the household flags and refetches on every live change (another
 * device flipping a switch shows up here within a few seconds).
 *
 * Skips the initial load when the landing route is a board: the board's own
 * request (T5, api.board.summary) always includes `flags` and applies it via
 * applyHouseholdFlags within moments of mounting, so a same-tick /api/flags
 * call here would be a second request for the same answer (T8,
 * docs/performance.md C2 — one fewer request on a board launch). Every other
 * route (Rewards, Schedules, ...) has no board on the page to supply flags,
 * so it still loads them itself.
 *
 * Also skips a later refetch (not the initial load, any route) when the
 * board applied a fresh copy of the same flags moments ago: the board's live
 * refresh already re-reads flags in the same D1 batch, so a second
 * /api/flags call right after would just repeat that read for no new
 * information. Screens with no board on this page load still stay in sync,
 * since a stale (> RECENT_APPLY_MS) apply no longer counts as fresh.
 */
export function useHouseholdFlagsSync(): void {
  useLive((initial) => {
    if (initial && isBoardRoute()) return;
    if (!initial && Date.now() - getLastAppliedAt() < RECENT_APPLY_MS) return;
    void loadHouseholdFlags();
  }, []);
}

/** Whether the current hash route renders a board (the two screens whose own request already carries the flags). */
function isBoardRoute(): boolean {
  const name = parseHash(location.hash).name;
  return name === "open" || name === "done";
}

/** Fetches the household flags and dispatches "op:flags" if the result differs from what's cached. */
export async function loadHouseholdFlags(): Promise<void> {
  try {
    applyHousehold(await api.flags.get());
  } catch {
    // Not logged in yet, or offline; keep the last known state and try again next live update.
  }
}

/** Sets one household flag for everyone. Stores the API's returned state and dispatches "op:flags". */
export async function setHouseholdFlag(key: FlagKey, enabled: boolean): Promise<Flags> {
  const next = await api.flags.set(key, enabled);
  applyHousehold(next);
  return next;
}

/** The current household flag state, re-rendering whenever it changes (locally or from another device). */
export function useHouseholdFlags(): Flags {
  const [flags, setFlags] = useState<Flags>(getHousehold);
  useEffect(() => {
    const update = () => setFlags(getHousehold());
    window.addEventListener(HOUSEHOLD_EVENT, update);
    update();
    return () => window.removeEventListener(HOUSEHOLD_EVENT, update);
  }, []);
  return flags;
}

/** Whether the household flags are known yet (see loadHouseholdFlags). */
export function useHouseholdFlagsLoaded(): boolean {
  const [loaded, setLoaded] = useState(isHouseholdLoaded);
  useEffect(() => {
    const update = () => setLoaded(isHouseholdLoaded());
    window.addEventListener(HOUSEHOLD_LOADED_EVENT, update);
    update();
    return () => window.removeEventListener(HOUSEHOLD_LOADED_EVENT, update);
  }, []);
  return loaded;
}

/** The features currently on for this device (either switch), in FLAG_KEYS order. */
export function useEnabledFeatures(): FlagKey[] {
  // FLAG_KEYS is a constant, so the hook order below is stable.
  const on = FLAG_KEYS.map((key) => useFlag(key));
  return FLAG_KEYS.filter((_, i) => on[i]);
}

/** A feature is visible when either this device's checkbox or the household switch is on. */
function useFlag(key: FlagKey): boolean {
  const local = useLocalFlag(key);
  const [householdOn, setHouseholdOn] = useState(() => getHouseholdFlag(key));
  useEffect(() => {
    const update = () => setHouseholdOn(getHouseholdFlag(key));
    window.addEventListener(HOUSEHOLD_EVENT, update);
    update();
    return () => window.removeEventListener(HOUSEHOLD_EVENT, update);
  }, [key]);
  return local || householdOn;
}

/** Effective bounty presentation: the Bounties switch, or Task economy (which implies it). */
export function useBountyUI(): boolean {
  const bountyUI = useFlag("bountyUI");
  const economyUI = useFlag("economyUI");
  return bountyUI || economyUI;
}
export function setBountyUIEnabled(value: boolean): void {
  setLocalFlag("bountyUI", value);
}

export function useNotesUI(): boolean {
  return useFlag("notesUI");
}
export function setNotesUIEnabled(value: boolean): void {
  setLocalFlag("notesUI", value);
}

export function useEconomyUI(): boolean {
  return useFlag("economyUI");
}
export function setEconomyUIEnabled(value: boolean): void {
  setLocalFlag("economyUI", value);
}

export function useFocusUI(): boolean {
  return useFlag("focusUI");
}
export function setFocusUIEnabled(value: boolean): void {
  setLocalFlag("focusUI", value);
}

/** Quick bonus presentation (flame badge, tutorial step). The payout itself is always on. */
export function useQuickUI(): boolean {
  return useFlag("quickUI");
}
export function setQuickUIEnabled(value: boolean): void {
  setLocalFlag("quickUI", value);
}

/** Original art and voice: original portraits/voice plus the new pixel mascot in the header. */
export function useOriginalAssets(): boolean {
  return useFlag("originalAssets");
}
export function setOriginalAssetsEnabled(value: boolean): void {
  setLocalFlag("originalAssets", value);
}

/** Animate the selected top-bar mascot. Original art uses discrete blink/speech frames. No tutorial steps. */
export function useLivePeon(): boolean {
  return useFlag("livePeon");
}
export function setLivePeonEnabled(value: boolean): void {
  setLocalFlag("livePeon", value);
}

/** Dynasty upgrades (docs/upgrades.md): the Upgrades tab, its three screens and its tour step. The server records regardless. */
export function useUpgradesUI(): boolean {
  return useFlag("upgradesUI");
}
export function setUpgradesUIEnabled(value: boolean): void {
  setLocalFlag("upgradesUI", value);
}

/** Passkeys and passwords (docs/members.md "People and passkeys", "Personal passwords"): the account sheet's Passkey and Password rows and the House screen's badges and People. The routes and the held-name rule work regardless. */
export function usePasskeysUI(): boolean {
  return useFlag("passkeys");
}

/** Monthly standings (docs/bounties.md "Monthly standings"): the Peon of the Month card's gold, silver and bronze rows. The server sends standings regardless. */
export function useStandingsUI(): boolean {
  return useFlag("standings");
}

/**
 * The season recap (src/recap.ts): the once-per-recap interstitial and the Peon of the Month card's
 * "Watch recap". Off by default (owner: "so it won't appear till I've finalized the video and tested
 * it out"); the server also leaves `recap` out of the summary while it is off.
 */
export const RECAP_FLAG: FlagKey = "recapUI";
export function useRecapUI(): boolean {
  return !!useFlag(RECAP_FLAG);
}
/** The same, read outside a component (the board's load, right after it applied the summary's flags). */
export function recapUIEnabled(): boolean {
  return !!isFeatureEnabled(RECAP_FLAG);
}

/**
 * Timed notes (docs/timed-notes.md): the note composer's "Alarm" and "Who" rows. The composer
 * lives inside pinned notes, so it shows only with notesUI on too. Timed notes already made still
 * fire, count down and ring whatever this says (data never depends on a UI flag).
 */
export function useTimersUI(): boolean {
  const timers = useFlag("timersUI");
  const notes = useFlag("notesUI");
  return timers && notes;
}

/**
 * The peon shop (shared/cosmetics.ts, docs/peon-look.md): the price chips and the buy confirm in the
 * character editor (#/peon). Whether a piece must be bought is the server's word (the wardrobe's
 * `shop`); this switch only says whether the prices show. While the household's flag is off the
 * server grants a piece free when it is worn, so the editor behaves as if everything were owned.
 */
export function useShopUI(): boolean {
  return useFlag("shopUI");
}

/** Achievements (docs/roadmap-2026-10.md): the badges on Profile, and the trophy shelf in the editor. */
export function useAchievementsUI(): boolean {
  return useFlag("achievementsUI");
}

/** Ranks from lifetime gold, under the name in Profile. */
export function useRanksUI(): boolean {
  return useFlag("ranksUI");
}

/** The purse: where the gold went, on the Rewards screen. */
export function usePurseUI(): boolean {
  return useFlag("purseUI");
}

/** "Me do it": claim a task for an hour so nobody doubles up. */
export function useClaimUI(): boolean {
  return useFlag("claimUI");
}

/** Nudge a peon about a task, once a day per task. */
export function useNudgeUI(): boolean {
  return useFlag("nudgeUI");
}

/** The weekly scroll: Sunday's push and the card on Done. */
export function useWeeklyUI(): boolean {
  return useFlag("weeklyUI");
}


/** Household wallpaper: a dynasty upgrade that changes the board's texture. */
export function useWallpaperUI(): boolean {
  return useFlag("wallpaperUI");
}

/** The bounty streak: a modest multiplier from the third day in a row. */
export function useStreakUI(): boolean {
  return useFlag("streakUI");
}

/** Gold rush pick: yesterday's lowest earner chooses tomorrow's gold rush task. */
export function useRushPickUI(): boolean {
  return useFlag("rushPickUI");
}

/** Luxury auction: an untaken luxury task's price rises a little each day. */
export function useAuctionUI(): boolean {
  return useFlag("auctionUI");
}

/** Wagers: two peons stake gold on who finishes next. */
export function useWagerUI(): boolean {
  return useFlag("wagerUI");
}

/** The Done gallery: after photos in a grid. */
export function useGalleryUI(): boolean {
  return useFlag("galleryUI");
}

/** The photo compare slider on a done task. */
export function useCompareUI(): boolean {
  return useFlag("compareUI");
}

/** Peon moods: idle animations by the board's state and the empty board celebration. */
export function usePeonMoodsUI(): boolean {
  return useFlag("peonMoodsUI");
}

/**
 * Every flag's effective value on this device (either switch), re-rendering on any change: what
 * What's new's About list reads (shared/whats-new.ts visibleWhatsNew), so it lists what the person can see.
 */
export function useEffectiveFlags(): Flags {
  const on = useEnabledFeatures();
  return Object.fromEntries(FLAG_KEYS.map((k) => [k, on.includes(k)])) as Flags;
}

/** Task roulette: a task nobody has touched for three days is spun to someone (docs/roulette.md). */
export function useRouletteUI(): boolean {
  return useFlag("rouletteUI");
}

/** Yours: the tasks assigned to you or that you're on, first on the open board (docs/yours.md). */
export function useYoursUI(): boolean {
  return useFlag("yoursUI");
}

/** Give credit: a done task's completion and gold handed to another peon, quietly (docs/credit.md). */
export function useGiveCreditUI(): boolean {
  return useFlag("giveCreditUI");
}

/** Trinkets: chores turn up trinkets, kept in the household's collection, turned in 3D with a tune each (docs/trinkets.md). */
export function useTrinketsUI(): boolean {
  return useFlag("trinketsUI");
}

/** Late chore rush: late chores come as one gold rush at each peon's usual hour (docs/bounties.md "Late chore rush"). */
export function useLateRushUI(): boolean {
  return useFlag("lateRushUI");
}

/** Note hearts: a heart on a pinned note, from anyone but its author (docs/notes.md "Hearts"). */
export function useHeartsUI(): boolean {
  return useFlag("heartsUI");
}

/** Name tags: a household name in a scroll line, a note or a task is tagged in that peon's colour, and "@" opens a picker (docs/mentions.md). */
export function useMentionsUI(): boolean {
  return useFlag("mentionsUI");
}

/** The speedrun note: Hue's newest personal best pinned to the notice board for good (docs/notes.md "The speedrun note"). */
export function useSpeedrunUI(): boolean {
  return useFlag("speedrunUI");
}

/** Photo notes: the composer can pin a photo with an optional caption (docs/notes.md "Photo notes"). */
export function usePhotoNotesUI(): boolean {
  return useFlag("photoNotesUI");
}

/** The slop tally: days without Omie's slop on a sign across the top of the notice board (docs/notes.md "The slop tally"). */
export function useSlopTallyUI(): boolean {
  return useFlag("slopTallyUI");
}

/** Suggest something: an idea or a bug, with up to 3 photos, from under About's version (docs/feedback.md). */
export function useFeedbackUI(): boolean {
  return useFlag("feedbackUI");
}

/** The daily reminder: one push a day at your usual hour naming the tasks the wheel picked you for (docs/roulette.md "The daily reminder"). The server sends it; no screen reads this yet. */
export function useYoursPushUI(): boolean {
  return useFlag("yoursPushUI");
}

/** Cheers: a cheer on a done task, and the line saying who cheered (docs/cheers.md). */
export function useCheersUI(): boolean {
  return useFlag("cheersUI");
}

/** Hello by name: the launch greeting says the peon's name (docs/voice.md "Lines by name"). src/audio.ts reads the flag itself; no screen reads this yet. */
export function useHelloUI(): boolean {
  return useFlag("helloUI");
}

/** The house goal (docs/house-week.md): one shared weekly target on the notice board. */
export function useHouseGoalUI(): boolean {
  return useFlag("houseGoalUI");
}

/** Themed weeks (docs/house-week.md): the week's theme on the notice board; its chores pay extra. */
export function useThemeWeekUI(): boolean {
  return useFlag("themeWeekUI");
}

/** Shared credit (docs/shares.md): Job's Done's "Who helped?" and Share credit on a done task; the meta line names everyone. */
export function useShareCreditUI(): boolean {
  return useFlag("shareCreditUI");
}

/** Projects (docs/projects.md): a big job reads as a project, with progress photos that grow its pot. */
export function useProjectsUI(): boolean {
  return useFlag("projectsUI");
}
